import { UserAppConfig } from '@flowwai-frontend/bps-officer-web/graphql'
import { useAsyncWithAuth } from '@flowwai-frontend/shared/auth/common'
import {
   ColorVariantsEnum,
   TaskCallStatusOptionsType,
   useAccountStore,
   usePipelineStore
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import {
   convertGqlStageTransitionStyleToPipelineStageTransitionStyle,
   convertPipeLineTypeToPipeLineTypeEnum,
   getConvertedColorEnum
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { convertFileUploaderUrlResponseToAttachmentModel } from '@flowwai-frontend/bps-officer-web/common/ui'

import {
   convertGraphqlFailedCallTypeToFailedCallTypeEnum,
   convertGraphqlPluginTypeToLocal,
   convertOutboundCallTypeToCallTypeEnum,
   getAccountFeaturesStatus,
   getFilteredActivePlugins,
   getPipelineFeaturesStatus
} from '../../factories'
import {
   GetUserAppConfigQueryResult,
   GetUserAppConfigQueryVariables
} from '../../query/GetUserAppConfig/query.generatedTypes'

import { useUserService } from '../services/useUserService'
import { useUserAppConfigStore } from '../stores/useUserAppConfigStore'

const useGetUserAppConfig = () => {
   const userService = useUserService()
   const userStore = useUserAppConfigStore()
   const pipelineStore = usePipelineStore()
   const accountStore = useAccountStore()

   const { triggerAPI, apiStatus, error: apiError } = useAsyncWithAuth<
      GetUserAppConfigQueryResult | unknown,
      Error,
      GetUserAppConfigQueryVariables
   >(userService.getUserAppConfig)

   const onSuccessAPI = (
      response: GetUserAppConfigQueryResult | unknown
   ): void => {
      if (typeof response !== 'object') return
      if (!response) return
      if (!('data' in response)) return

      const { data } = response as GetUserAppConfigQueryResult
      if (!data) return

      const userAppConfig = data.getUserAppConfig as UserAppConfig

      if (!userAppConfig) return

      accountStore.setAccountName(userAppConfig.name)

      accountStore.setAccountId(userAppConfig.userPropertiesTemplate.accountId)

      accountStore.setUserPropertiesTemplateId(
         userAppConfig.userPropertiesTemplate.userPropertiesTemplateId
      )

      const salesPipelinesIds: string[] = []
      userAppConfig.pipelines.forEach(pipeline => {
         salesPipelinesIds.push(pipeline.pipelineId)

         pipelineStore.createOrUpdatePipeline({
            pipelineId: pipeline.pipelineId,
            pipelineItemType: convertPipeLineTypeToPipeLineTypeEnum(
               pipeline.pipelineItemType
            ),
            name: pipeline.name,
            displayName: pipeline.displayName,
            pipelineItemTemplateId: pipeline.pipelineItemTemplateId,
            pipelineIcon: {
               iconId: pipeline.pipelineIconId ?? undefined,
               iconColor: pipeline.color ?? undefined
            },
            stageFieldId: pipeline.pipelineItemTemplateConfig.stageFieldId,
            displayStageFieldId:
               pipeline.pipelineItemTemplateConfig.displayStageFieldId ??
               undefined,
            stageTransitionView: convertGqlStageTransitionStyleToPipelineStageTransitionStyle(
               pipeline.stageTransitionStyle
            )
         })
      })

      const callingPermissions = userAppConfig.callingPermissions
      switch (callingPermissions.__typename) {
         case 'CallingPermissions':
            userStore.setCallingPermission({
               hasCallingPermission: true,
               callTypeEnum: convertOutboundCallTypeToCallTypeEnum(
                  callingPermissions.allowedCallingType
               ),
               failedCallTypeEnum: callingPermissions.allowDirectCallingForFailedCloudCalling
                  ? convertGraphqlFailedCallTypeToFailedCallTypeEnum(
                       callingPermissions.allowDirectCallingForFailedCloudCalling
                    )
                  : null
            })
            break
         case 'NoCallingPermission':
            userStore.setCallingPermission({
               hasCallingPermission: false,
               callTypeEnum: null,
               failedCallTypeEnum: null
            })
            break
      }

      const activePluginEnums = userAppConfig.activePluginEnums
      const activePlugins = getFilteredActivePlugins(activePluginEnums)

      userStore.addActivePlugins(activePlugins)

      userStore.addSalesPipelineIds(salesPipelinesIds)

      const taskStatusCallOptions = userAppConfig.taskCallStatusOptions

      taskStatusCallOptions.forEach(callStatusOption => {
         const color = getConvertedColorEnum(callStatusOption.color)
         const option: TaskCallStatusOptionsType = {
            color: color ? color : ColorVariantsEnum.Blue,
            option: callStatusOption.option
         }
         pipelineStore.createTaskCallStatusOption(option)
      })

      const pipelineActivityPermissions =
         userAppConfig.pipelineActivitiesInitiateLeadPermissions
      pipelineActivityPermissions.forEach(pipeline => {
         userStore.setPipelineActivityPermissions(pipeline.pipelineId, {
            initiateLeadActivities: pipeline.activityTemplateIds
         })
      })

      const personalMessagePermissions =
         userAppConfig.pipelineWisePersonalWhatsappMessageStatus
      personalMessagePermissions.forEach(pipeline => {
         userStore.setPipelinePersonalMessagePermission(
            pipeline.pipelineId,
            pipeline.hasPersonalWhatsappMessagePermission
         )
      })

      userAppConfig.pipelineWiseFeatures.forEach(pipelineFeature => {
         const {
            isActivitiesEnabled,
            isCallLogsEnabled,
            isSendWhatsAppMessagesEnabled,
            isShareableResourcesEnabled,
            isTasksEnabled,
            isRecordScoreEnabled
         } = getPipelineFeaturesStatus(pipelineFeature.features)

         userStore?.permissions?.addPipelineCustomization(
            pipelineFeature.pipelineId,
            {
               enableActivities: isActivitiesEnabled,
               enableCalls: isCallLogsEnabled,
               enableSendWhatsAppMessages: isSendWhatsAppMessagesEnabled,
               enableShareableResources: isShareableResourcesEnabled,
               enableTasks: isTasksEnabled,
               enableRecordScore: isRecordScoreEnabled
            }
         )
      })

      userAppConfig.pipelines.forEach(pipeline => {
         userStore.setPipelineWhatsappConsentRequireStatus(
            pipeline.pipelineId,
            pipeline.isWhatsappConsentRequired
         )
      })

      const {
         isAIChatSupportEnabled,
         isContactsEnabled,
         isDataCoPilotEnabled,
         isSchedulingEnabled,
         isGoogleCalendarIntegrationEnabled,
         isFlowwSignEnabled,
         isDocuSignEnabled,
         isAttendanceTrackingEnabled,
         isOfficerManagementEnabled,
         isApplicationManagementEnabled,
         isTdrManagementEnabled,
         isScrutinyKeyGenerationEnabled,
         isLtpUserManagementEnabled
      } = getAccountFeaturesStatus(userAppConfig.accountFeatures)

      userStore.permissions?.setIsContactsEnabled(isContactsEnabled)
      userStore.permissions?.setIsSchedulingEnabled(isSchedulingEnabled)
      userStore.permissions?.setIsGoogleCalendarIntegrationEnabled(
         isGoogleCalendarIntegrationEnabled
      )
      userStore.permissions?.setIsDocuSignIntegrationEnabled(isDocuSignEnabled)
      userStore.permissions?.setIsFlowwSignIntegrationEnabled(
         isFlowwSignEnabled
      )
      userStore.permissions?.setIsAttendanceTrackingEnabled(
         isAttendanceTrackingEnabled
      )
      userStore.permissions?.setIsAIChatSupportEnabled(isAIChatSupportEnabled)
      userStore.permissions?.setIsDataCiPilotEnabled(isDataCoPilotEnabled)
      userStore.permissions?.setUserRole(
         isOfficerManagementEnabled ? 'PMU_OFFICER' : 'OFFICER'
      )
      userStore.permissions?.setIsApplicationManagementEnabled(
         isApplicationManagementEnabled
      )
      userStore.permissions?.setIsTdrManagementEnabled(isTdrManagementEnabled)
      userStore.permissions?.setIsLtpUserManagementEnabled(
         isLtpUserManagementEnabled
      )
      userStore.permissions?.setIsScrutinyKeyGenerationEnabled(
         isScrutinyKeyGenerationEnabled
      )

      userAppConfig.kmlFiles.forEach(kmlFile => {
         userStore.addKmlFile(
            convertFileUploaderUrlResponseToAttachmentModel(kmlFile)
         )
      })
   }

   const doNetworkCall = (
      onSuccess?: () => void,
      onFailure?: (error: Error) => void
   ): void => {
      triggerAPI(
         {},
         {
            onSuccess: (response: GetUserAppConfigQueryResult | unknown) => {
               onSuccessAPI(response)
               if (response && onSuccess) {
                  onSuccess()
               }
            },
            onError: onFailure
         }
      )
   }

   return {
      getUserAppConfigAPITrigger: doNetworkCall,
      getUserAppConfigAPIStatus: apiStatus,
      getUserAppConfigAPIError: apiError
   }
}

export { useGetUserAppConfig }
